// openEHR RM model — BMM package org.openehr.rm.ehr
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { T } from "../_shared.ts";
import { LOCATABLE } from "../common/archetyped.ts";
import { VERSIONED_OBJECT } from "../common/change_control.ts";
import type { PARTY_SELF } from "../common/generic.ts";
import type { ITEM_STRUCTURE } from "../data_structures/item_structure.ts";
import type { DV_DATE_TIME } from "../data_types/quantity/date_time.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * The EHR object is the root object and access point of an EHR for a subject of care.
 */
export class EHR {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * The identifier of the logical EHR management system in which this EHR was created.
   */
  system_id?: openehr_base.HIER_OBJECT_ID;
  /**
   * The unique identifier of this EHR.
   *
   * NOTE: is is strongly recommended that a UUID always be used for this field.
   */
  ehr_id?: openehr_base.HIER_OBJECT_ID;
  /**
   * List of contributions causing changes to this EHR. Each contribution contains a list of versions, which may include references to any number of \`VERSION\` instances, i.e. items of type \`VERSIONED_COMPOSITION\` and \`VERSIONED_FOLDER\`.
   */
  contributions?: undefined;
  /**
   * Reference to \`EHR_STATUS\` object for this EHR.
   */
  ehr_status?: openehr_base.OBJECT_REF;
  /**
   * Reference to \`EHR_ACCESS\` object for this EHR.
   */
  ehr_access?: openehr_base.OBJECT_REF;
  /**
   * Master list of all Versioned Composition references in this EHR.
   */
  compositions?: undefined;
  /**
   * Optional directory structure for this EHR. If present, this is a reference to the first member of \`_folders_\`.
   */
  directory?: openehr_base.OBJECT_REF;
  /**
   * Time of creation of the EHR.
   */
  time_created?: DV_DATE_TIME;
  /**
   * Optional additional Folder structures for this EHR. If set, the \`_directory_\` attribute refers to the first member.
   */
  folders?: undefined;
  /**
   * Optional list of tags associated with this EHR. Tag \`_target_\` values can only be within the same EHR.
   */
  tags?: undefined;
}

/**
 * Version container for \`EHR_ACCESS\` instance.
 */
export class VERSIONED_EHR_ACCESS extends VERSIONED_OBJECT<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Version container for \`EHR_STATUS\` instance.
 */
export class VERSIONED_EHR_STATUS extends VERSIONED_OBJECT<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Version-controlled composition abstraction, defined by inheriting \`VERSIONED_OBJECT<COMPOSITION>\`.
 */
export class VERSIONED_COMPOSITION extends VERSIONED_OBJECT<T> {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Indicates whether this composition set is persistent; derived from first version.
   * @returns Result value
   */
  is_persistent(): openehr_base.Boolean {
    // Note: Should check the first version's composition.is_persistent()
    // This requires version management infrastructure
    throw new Error("VERSIONED_COMPOSITION.is_persistent requires version retrieval - not yet fully implemented");
  }
}

/**
 * EHR-wide access control object. All access decisions to data in the EHR must be made in accordance with the policies and rules in this object.
 *
 * NOTE: It is strongly recommended that the inherited attribute \`_uid_\` be populated in \`EHR_ACCESS\` objects, using the UID copied from the \`_object_id()_\` of the \`_uid_\` field of the enclosing \`VERSION\` object. +
 * For example, the \`ORIGINAL_VERSION.uid\` \`87284370-2D4B-4e3d-A3F3-F303D2F4F34B::uk.nhs.ehr1::2\` would be copied to the \`_uid_\` field of the \`EHR_ACCESS\` object.
 */
export class EHR_ACCESS extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Access control settings for the EHR. Instance is a subtype of the type \`ACCESS_CONTROL_SETTINGS\`, allowing for the use of different access control schemes.
   */
  settings?: ACCESS_CONTROL_SETTINGS;
  /**
   * The name of the access control scheme in use; corresponds to the concrete instance of the settings attribute.
   * @returns Result value
   */
  scheme(): openehr_base.String {
    // Return the class name of the settings instance
    if (!this.settings) {
      throw new Error("EHR_ACCESS settings is required to determine scheme");
    }
    return openehr_base.String.from(this.settings.constructor.name);
  }
}

/**
 * Single object per EHR containing various EHR-wide status flags and settings, including whether this EHR can be queried, modified etc. This object is always modifiable, in order to change the status of the EHR as a whole.
 *
 * NOTE: It is strongly recommended that the inherited attribute \`_uid_\` be populated in \`EHR_STATUS\` objects, using the UID copied from the \`_object_id()_\` of the \`_uid_\` field of the enclosing \`VERSION\` object. +
 * For example, the \`ORIGINAL_VERSION.uid\` \`87284370-2D4B-4e3d-A3F3-F303D2F4F34B::uk.nhs.ehr1::2\`  would be copied to the \`_uid_\` field of the \`EHR_STATUS\` object.
 */
export class EHR_STATUS extends LOCATABLE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The subject of this EHR. The \`_external_ref_\` attribute can be used to contain a direct reference to the subject in a demographic or identity service. Alternatively, the association between patients and their records may be done elsewhere for security reasons.
   */
  subject?: PARTY_SELF;
  /**
   * Internal storage for is_queryable
   * @protected
   */
  protected _is_queryable?: openehr_base.Boolean;

  /**
   * True if this EHR should be included in population queries, i.e. if this EHR is considered active in the population.
   */
  get is_queryable(): boolean | undefined {
    return this._is_queryable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_queryable.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_queryable(): openehr_base.Boolean | undefined {
    return this._is_queryable;
  }

  /**
   * Sets is_queryable from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_queryable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_queryable = undefined;
    } else if (typeof val === "boolean") {
      this._is_queryable = openehr_base.Boolean.from(val);
    } else {
      this._is_queryable = val;
    }
  }

  /**
   * Internal storage for is_modifiable
   * @protected
   */
  protected _is_modifiable?: openehr_base.Boolean;

  /**
   * True if the EHR, other than the \`EHR_STATUS\` object, is allowed to be written to. The \`EHR_STATUS\` object itself can always be written to.
   */
  get is_modifiable(): boolean | undefined {
    return this._is_modifiable?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_modifiable.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_modifiable(): openehr_base.Boolean | undefined {
    return this._is_modifiable;
  }

  /**
   * Sets is_modifiable from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_modifiable(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_modifiable = undefined;
    } else if (typeof val === "boolean") {
      this._is_modifiable = openehr_base.Boolean.from(val);
    } else {
      this._is_modifiable = val;
    }
  }

  /**
   * Any other details of the EHR summary object, in the form of an archetyped \`ITEM_STRUCTURE\`.
   */
  other_details?: ITEM_STRUCTURE;
}

/**
 * Access Control Settings for the EHR and components. Intended to support multiple access control schemes. Currently implementation dependent.
 */
export abstract class ACCESS_CONTROL_SETTINGS {
}
